<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.programmings', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                required
                                id="title"
                                v-model="form.title"
                                name="title"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.title" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.trimester')" for="trimester" />
                            <FormSelect
                                required
                                id="trimester"
                                v-model="form.trimester"
                                :options="options.trimesters"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.trimester" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.stages', 1)" for="stage_id" />
                            <FormSelect
                                required
                                id="stage_id"
                                v-model="form.stage_id"
                                :options="options.stages"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.stage_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids" />
                        <div class="mt-1">
                            <SlideOverCheckboxInput v-model="form.grade_level_ids" :options="filteredGradeLevels" />
                        </div>
                        <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                    </div>

                    <div class="sm:col-span-4">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.groups') }}
                        </label>
                        <div class="mt-1 rounded-md shadow-sm" v-if="groups.length">
                            <label class="ml-1 mr-3" v-for="group in groups" :key="group.id">
                                <input
                                    name="groups[]"
                                    type="checkbox"
                                    :value="group.id"
                                    v-model="form.group_ids"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                    {{ group.name }}
                                </span>
                            </label>
                        </div>
                        <FormError :value="form.errors.group_ids" class="mt-2" />
                    </div>

                    <div class="sm:col-span-6">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $tc('messages.subject', 2) }}
                        </label>
                        <div v-if="!form.grade_level_ids" class="mt-1 rounded-md shadow-sm">
                            {{ $t('messages.select') }}
                            {{ $t('messages.grade_level') }}
                        </div>
                        <div v-else class="mt-1 rounded-md shadow-sm">
                            <div class="flex flex-wrap space-x-2">
                                <BadgePill v-for="subject in selectedSubjects" :key="subject.id">
                                    {{ subject.name }}
                                </BadgePill>
                            </div>

                            <AppButton
                                element="button"
                                type="button"
                                class="mt-1"
                                @click="showSubjectsSlideOver = !showSubjectsSlideOver"
                            >
                                {{ $t('messages.edit') }}
                            </AppButton>
                            <FormError :value="form.errors.subject_ids" class="mt-2" />
                        </div>
                        <SlideOver :show="showSubjectsSlideOver" @close="showSubjectsSlideOver = false">
                            <ul>
                                <li v-for="subject in filteredSubjects" :key="subject.id">
                                    <label class="ml-1 mr-3">
                                        <input
                                            name="subjects[]"
                                            type="checkbox"
                                            :value="subject.id"
                                            v-model="form.subject_ids"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                            {{ subject.name }}
                                        </span>
                                    </label>
                                </li>
                            </ul>
                        </SlideOver>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.short_description') }}
                </template>
                <FormLabel :value="$t('messages.short_description')" for="short_description" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="short_description"
                    v-model="form.short_description"
                    name="short_description"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.short_description" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.methodology') }}
                </template>
                <FormLabel :value="$t('messages.methodology')" for="methodology" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="methodology"
                    v-model="form.methodology"
                    name="methodology"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.methodology" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.diversity_observations') }}
                </template>
                <FormLabel
                    :value="$t('messages.diversity_observations')"
                    for="diversity_observations"
                    class="sr-only"
                />
                <FormTextarea
                    rows="3"
                    id="diversity_observations"
                    v-model="form.diversity_observations"
                    name="diversity_observations"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.diversity_observations" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.learning_aims') }}
                </template>
                <FormLabel :value="$t('messages.learning_aims')" for="learning_aims" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="learning_aims"
                    v-model="form.learning_aims"
                    name="learning_aims"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.learning_aims" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.general_contents') }}
                </template>
                <FormLabel :value="$t('messages.general_contents')" for="general_contents" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="general_contents"
                    v-model="form.general_contents"
                    name="general_contents"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.general_contents" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection class="hidden">
                <template v-slot:title>
                    {{ $t('messages.evaluation_criteria') }}
                </template>
                <FormLabel :value="$t('messages.evaluation_criteria')" for="evaluation_criteria" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="evaluation_criteria"
                    v-model="form.evaluation_criteria"
                    name="evaluation_criteria"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.evaluation_criteria" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.general_competences') }}
                </template>
                <FormLabel :value="$t('messages.general_competences')" for="general_competences" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="general_competences"
                    v-model="form.general_competences"
                    name="general_competences"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.general_competences" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.learning_vectors') }}
                </template>
                <FormLabel :value="$t('messages.learning_vectors')" for="learning_vectors" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="learning_vectors"
                    v-model="form.learning_vectors"
                    name="learning_vectors"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.learning_vectors" class="mt-2" />
            </TitleFieldsFormSection>

            <div>
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import BadgePill from '@/components/BadgePill.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import AppButton from '@/components/AppButton.vue';
import SlideOver from '@/components/SlideOver.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';

export default {
    components: {
        FormTextarea,
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        BadgePill,
        FormLabel,
        SlideOverCheckboxInput,
        AppButton,
        SlideOver,
        FormInput,
        FormSelect,
        FormError,
    },
    props: ['meta', 'defaults', 'options', 'subjects'],
    layout: Layout,
    data() {
        return {
            selected_subject: null,
            form: this.$inertia.form({
                title: '',
                grade_level_ids: this.defaults.grade_level_ids,
                stage_id: this.defaults.stage_id,
                group_ids: [],
                subject_ids: [],
                trimester: this.defaults.trimester,
                methodology: '',
                diversity_observations: '',
                short_description: '',
                general_contents: '',
                evaluation_criteria: '',
                learning_aims: '',
                general_competences: '',
                learning_vectors: '',
                subjects: [],
            }),
            showSubjectsSlideOver: false,
        };
    },
    name: 'Create',
    computed: {
        groups: function () {
            return this.form.grade_level_ids
                ? this.options.gradeLevels
                      .filter((gl) => this.form.grade_level_ids.includes(gl.id))
                      .flatMap((gl) => gl.groups)
                : [];
        },
        filteredGradeLevels: function () {
            return this.options.gradeLevels.filter((s) => s.stage_id == this.form.stage_id);
        },
        filteredSubjects: function () {
            return this.options.subjects.filter((s) => this.form.stage_id == s.stage_id);
        },
        selectedSubjects: function () {
            return this.options.subjects.filter((s) => this.form.subject_ids.includes(s.id));
        },
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        addSession() {
            this.form.sessions.push({
                title: '',
            });
        },
    },
    watch: {
        'form.stage_id': function (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.form.grade_level_ids = [];
                this.form.subject_ids = [];
            }
        },
        showSubjectsSlideOver: function (open) {
            if (open === false) {
                if (!this.selectedSubjects.map((s) => s.id).includes(this.selected_subject)) {
                    this.selected_subject = this.selectedSubjects.map((s) => s.id)[0];
                }
            }
        },
    },
};
</script>
